import type { LucideIcon } from "lucide-react";
import {
  ArrowDownToLine,
  Box,
  Brain,
  Camera,
  ClipboardList,
  FolderTree,
  Gavel,
  Gift,
  Landmark,
  Layers,
  LayoutDashboard,
  Package,
  Puzzle,
  Receipt,
  ScrollText,
  Settings,
  Shield,
  Tag,
  Truck,
  FileStack,
  FileText,
  GalleryHorizontal,
  Store,
  Tags,
  Ticket,
  Users,
} from "lucide-react";
import { SETTINGS_TABS } from "./admin-settings";
import { FLEXXI_MODULE_ENTRY, FLEXXI_NAV_ITEMS, FLEXXI_NAV_SECTIONS } from "./flexxi-module";
import { MARKETPLACE_MODULE_ENTRY, MARKETPLACE_NAV_ITEMS, MARKETPLACE_NAV_SECTIONS } from "./marketplace-module";
import { VERIFICATION_NAV_ENTRY } from "./verification-module";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  perm?: string;
  /** Show when any listed permission passes (module rules still apply). */
  permAny?: string[];
  module?: string;
  /** Show when any listed module is enabled. */
  moduleAny?: string[];
  keywords?: string[];
  group?: "navigation" | "admin" | "action";
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export const MAIN_NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        to: "/",
        label: "Dashboard",
        icon: LayoutDashboard,
        perm: "dashboard.view",
        keywords: ["home", "workspace"],
      },
    ],
  },
  {
    title: "Sales",
    items: [
      { to: "/orders", label: "Orders", icon: Receipt, perm: "orders.read", keywords: ["order", "orders", "payment", "tracking"] },
      { to: "/fulfilment", label: "Fulfilment", icon: Package, perm: "orders.fulfil" },
      { to: "/packing-boxes", label: "Packing boxes", icon: Box, perm: "orders.fulfil", keywords: ["box", "parcel", "shipping"] },
      { to: "/receipts", label: "Bank Receipts", icon: Landmark, perm: "payments.verify_receipt", keywords: ["payment", "receipt", "bank", "transfer"] },
    ],
  },
  {
    title: "Catalog",
    items: [
      { to: "/products", label: "Products", icon: Layers, perm: "catalog.read", keywords: ["product", "sku", "catalog", "item", "warehouse"] },
      { to: "/product-types", label: "Product types", icon: Tags, perm: "catalog.read" },
      { to: "/categories", label: "Categories", icon: FolderTree, perm: "catalog.read" },
      { to: "/inventory", label: "Inventory", icon: ArrowDownToLine, perm: "catalog.stock" },
    ],
  },
  {
    title: "Utils",
    items: [
      { to: "/discounts", label: "Discounts", icon: Tag, perm: "discounts.read" },
      { to: "/shipping", label: "Shipping", icon: Truck, perm: "settings.read", keywords: ["delivery", "rates", "state", "country"] },
      {
        to: "/card-uploader",
        label: "Card Uploader",
        icon: Camera,
        perm: "carduploader.use",
        module: "card_uploader",
      },
    ],
  },
  {
    title: "Mechanics",
    items: [
      { to: "/shop-listings", label: "Shop listings", icon: Store, perm: "catalog.read", module: "ecommerce", keywords: ["shop", "storefront", "ecommerce", "listing"] },
      { to: "/raffles", label: "Raffles", icon: Ticket, perm: "raffles.read", module: "raffles" },
      { to: "/auctions", label: "Auctions", icon: Gavel, perm: "auctions.read", module: "auctions" },
      { to: "/packs", label: "Packs", icon: Gift, perm: "packs.read", module: "packs" },
    ],
  },
  {
    title: "Commerce",
    items: [MARKETPLACE_MODULE_ENTRY, FLEXXI_MODULE_ENTRY, VERIFICATION_NAV_ENTRY],
  },
  {
    title: "Customers",
    items: [
      { to: "/users", label: "Users", icon: Users, perm: "users.read" },
      { to: "/cases", label: "Cases", icon: Ticket, perm: "ai.cases", module: "support_forms" },
    ],
  },
  {
    title: "Content",
    items: [
      {
        to: "/content/slider",
        label: "Slider",
        icon: GalleryHorizontal,
        perm: "cms.read",
        module: "cms",
        keywords: ["carousel", "hero", "banner", "slider", "home"],
      },
      {
        to: "/content/pages",
        label: "Page",
        icon: FileText,
        perm: "cms.read",
        module: "cms",
        keywords: ["cms", "pages", "articles", "marketing", "storefront"],
      },
    ],
  },
];

/** Curated shortcuts for the fixed bottom navbar (flat list, no section dividers). */
export const BOTTOM_NAV_SHORTCUTS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view" },
  { to: "/orders", label: "Orders", icon: Receipt, perm: "orders.read" },
  { to: "/products", label: "Products", icon: Layers, perm: "catalog.read" },
  { to: "/shop-listings", label: "Shop", icon: Store, perm: "catalog.read", module: "ecommerce" },
  { to: "/inventory", label: "Inventory", icon: ArrowDownToLine, perm: "catalog.stock" },
  { to: "/raffles", label: "Raffles", icon: Ticket, perm: "raffles.read", module: "raffles" },
  { to: "/auctions", label: "Auctions", icon: Gavel, perm: "auctions.read", module: "auctions" },
  { to: "/packs", label: "Packs", icon: Gift, perm: "packs.read", module: "packs" },
];

export const ADMIN_SETTINGS_NAV_ITEMS: NavItem[] = SETTINGS_TABS.map((entry) => ({
  to: `/admin/settings/${entry.slug}`,
  label: entry.label,
  icon: entry.icon,
  perm: "settings.read",
  group: "admin" as const,
  keywords: [entry.label.toLowerCase(), entry.slug, "settings", "configuration"],
  ...(entry.module ? { module: entry.module } : {}),
  ...(entry.moduleAny ? { moduleAny: entry.moduleAny } : {}),
}));

export const ADMIN_AI_NAV_ITEMS: NavItem[] = [
  {
    to: "/admin/ai/models",
    label: "Models & Knowledge",
    icon: Brain,
    perm: "ai.configure",
    module: "ai_copilot_bo",
    group: "admin",
    keywords: ["ai", "models", "knowledge", "llm", "openai"],
  },
];

export const ADMIN_SUPPORT_NAV_ITEMS: NavItem[] = [
  {
    to: "/admin/support/forms",
    label: "Dynamic Forms",
    icon: ClipboardList,
    perm: "ai.forms",
    module: "support_forms",
    group: "admin",
    keywords: ["support", "forms", "dynamic", "workflow", "cases", "tickets"],
  },
];

export const ADMIN_ACCESS_NAV_ITEMS: NavItem[] = [
  {
    to: "/admin/document-templates",
    label: "Document templates",
    icon: FileStack,
    perm: "documents.write",
    module: "documents",
    group: "admin",
    keywords: ["invoice", "receipt", "pdf", "tax", "documents"],
  },
  {
    to: "/admin/roles",
    label: "Roles & Access",
    icon: Shield,
    perm: "rbac.manage",
    group: "admin",
    keywords: ["permissions", "rbac"],
  },
  {
    to: "/admin/modules",
    label: "Modules",
    icon: Puzzle,
    perm: "rbac.modules",
    group: "admin",
    keywords: ["features", "plugins"],
  },
  {
    to: "/admin/audit",
    label: "Audit Log",
    icon: ScrollText,
    perm: "rbac.manage",
    group: "admin",
    keywords: ["logs", "history"],
  },
];

export const ADMIN_NAV_SECTIONS: NavSection[] = [
  { title: "Settings", items: ADMIN_SETTINGS_NAV_ITEMS },
  { title: "AI", items: ADMIN_AI_NAV_ITEMS },
  { title: "Support", items: ADMIN_SUPPORT_NAV_ITEMS },
  { title: "Access", items: ADMIN_ACCESS_NAV_ITEMS },
];

/** Flat list for command palette, permissions, and tab titles */
export const ADMIN_NAV_ITEMS: NavItem[] = [
  ...ADMIN_SETTINGS_NAV_ITEMS,
  ...ADMIN_AI_NAV_ITEMS,
  ...ADMIN_SUPPORT_NAV_ITEMS,
  ...ADMIN_ACCESS_NAV_ITEMS,
];

export const ADMIN_CONFIG_ENTRY: NavItem = {
  to: "/admin/settings/general",
  label: "Admin Configuration",
  icon: Settings,
  group: "admin",
  keywords: ["admin", "settings", "roles", "modules", "ai"],
};

type CanFn = (permission: string) => boolean;
type ModuleFn = (key: string) => boolean;

export function filterNavItem(item: NavItem, can: CanFn, moduleEnabled: ModuleFn): boolean {
  if (item.permAny?.length) {
    if (!item.permAny.some((p) => can(p))) return false;
  } else if (item.perm && !can(item.perm)) {
    return false;
  }
  if (item.moduleAny?.length) {
    if (!item.moduleAny.some((key) => moduleEnabled(key))) return false;
  } else if (item.module && !moduleEnabled(item.module)) {
    return false;
  }
  return true;
}

function filterHubNavSections(sections: NavSection[], can: CanFn, moduleEnabled: ModuleFn): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => filterNavItem(item, can, moduleEnabled)),
    }))
    .filter((section) => section.items.length > 0);
}

function firstHubPath(sections: NavSection[], can: CanFn, moduleEnabled: ModuleFn): string | null {
  for (const section of filterHubNavSections(sections, can, moduleEnabled)) {
    const first = section.items[0];
    if (first) return first.to;
  }
  return null;
}

export function filterMarketplaceNavSections(can: CanFn, moduleEnabled: ModuleFn): NavSection[] {
  return filterHubNavSections(MARKETPLACE_NAV_SECTIONS, can, moduleEnabled);
}

export function filterFlexxiNavSections(can: CanFn, moduleEnabled: ModuleFn): NavSection[] {
  return filterHubNavSections(FLEXXI_NAV_SECTIONS, can, moduleEnabled);
}

export function flattenMarketplaceNavItems(): NavItem[] {
  return MARKETPLACE_NAV_ITEMS;
}

export function flattenFlexxiNavItems(): NavItem[] {
  return FLEXXI_NAV_ITEMS;
}

export function firstMarketplacePath(can: CanFn, moduleEnabled: ModuleFn): string | null {
  return firstHubPath(MARKETPLACE_NAV_SECTIONS, can, moduleEnabled);
}

export function firstFlexxiPath(can: CanFn, moduleEnabled: ModuleFn): string | null {
  return firstHubPath(FLEXXI_NAV_SECTIONS, can, moduleEnabled);
}

export function filterMainNavSections(can: CanFn, moduleEnabled: ModuleFn): NavSection[] {
  return MAIN_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => filterNavItem(item, can, moduleEnabled)),
  })).filter((section) => section.items.length > 0);
}

export function filterBottomNavShortcuts(can: CanFn, moduleEnabled: ModuleFn): NavItem[] {
  const items = BOTTOM_NAV_SHORTCUTS.filter((item) => filterNavItem(item, can, moduleEnabled));
  if (hasAdminAccess(can)) {
    items.push({ ...ADMIN_CONFIG_ENTRY, label: "Admin" });
  }
  return items;
}

export function filterAdminNavItems(can: CanFn, moduleEnabled: ModuleFn): NavItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => filterNavItem(item, can, moduleEnabled));
}

export function filterAdminNavSections(can: CanFn, moduleEnabled: ModuleFn): NavSection[] {
  return ADMIN_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => filterNavItem(item, can, moduleEnabled)),
  })).filter((section) => section.items.length > 0);
}

export function flattenMainNavItems(sections: NavSection[] = MAIN_NAV_SECTIONS): NavItem[] {
  return sections.flatMap((section) => section.items);
}

export function allRoutableNavItems(can: CanFn, moduleEnabled: ModuleFn): NavItem[] {
  return [
    ...flattenMainNavItems(filterMainNavSections(can, moduleEnabled)),
    ...flattenMarketplaceNavItems().filter((item) => filterNavItem(item, can, moduleEnabled)),
    ...flattenFlexxiNavItems().filter((item) => filterNavItem(item, can, moduleEnabled)),
    ...filterAdminNavItems(can, moduleEnabled),
  ];
}

export function hasAdminAccess(can: CanFn): boolean {
  return ADMIN_NAV_ITEMS.some((item) => !item.perm || can(item.perm));
}

export function firstAdminPath(can: CanFn): string | null {
  const item = ADMIN_NAV_ITEMS.find((entry) => !entry.perm || can(entry.perm));
  return item?.to ?? null;
}

export function resolveNavTitle(path: string): string {
  if (path === "/") return "Dashboard";
  if (path === "/notifications") return "Notifications";

  if (path.startsWith("/fulfilment/picklists/")) return "Pick list";

  const allItems = [...flattenMainNavItems(), ...ADMIN_NAV_ITEMS];
  const exact = allItems.find((item) => item.to === path);
  if (exact) return exact.label;

  if (path.startsWith("/marketplace")) {
    return "Marketplace";
  }
  if (path.startsWith("/flexxi")) {
    return "FlexxiPlan";
  }
  if (path.startsWith("/commerce/verifications")) return "Verification";
  if (path.startsWith("/commerce")) {
    return "Marketplace";
  }

  if (path.startsWith("/admin")) {
    const adminMatch = ADMIN_NAV_ITEMS.filter((item) => path.startsWith(item.to)).sort(
      (a, b) => b.to.length - a.to.length,
    )[0];
    return adminMatch?.label ?? "Admin Configuration";
  }

  const prefixMatch = allItems
    .filter((item) => item.to !== "/" && path.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefixMatch?.label ?? "Workspace";
}

export function absoluteNavUrl(path: string): string {
  return `${globalThis.location.origin}${path}`;
}
